import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  HelpBanner,
  Input,
  Label,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@betstarters/ui'
import { Ban, Pencil, Plus, RotateCcw } from 'lucide-react'
import { api } from '../api'
import { useCapabilities } from '../hooks/useCapabilities'
import { useSettingTemplates } from '../hooks/useReference'
import { messageOf } from '../lib/apiError'
import { CatalogToolbar, ErrorBanner, PageHeading, StatusBadge } from '../components/catalog'
import { Combobox } from '../components/controls'
import { Tab, TabCount, Tabs, TabsList, TabsPanel } from '../components/tabs'

export interface RuleThreshold {
  thresholdTypeId: number
  thresholdType: string
  thresholdSubTypeId: number
  thresholdSubType: string
  dataType: string
  templateTypeId: number
  templateType: string
  settingCount: number
  isDisabledByPlatform: boolean
  creationDate: string
  lastModificationDate: string | null
}

export interface RuleThresholdSubType {
  thresholdSubTypeId: number
  thresholdSubType: string
  dataType: string
  thresholdCount: number
  isDisabledByPlatform: boolean
  creationDate: string
  lastModificationDate: string | null
}

interface ThresholdForm {
  thresholdType: string
  templateTypeId: string
  thresholdSubTypeId: string
}

interface SubTypeForm {
  thresholdSubType: string
  dataType: string
}

const emptyThreshold: ThresholdForm = {
  thresholdType: '',
  templateTypeId: '',
  thresholdSubTypeId: '',
}
const emptySubType: SubTypeForm = { thresholdSubType: '', dataType: '' }

/**
 * RuleThresholdsPage is the leaf of the rules engine: the numeric limits a
 * setting accepts, and the five sub-types that say how a limit is READ.
 *
 * Two tables, one page, because neither stands alone — a threshold is a name
 * plus a data type, and the sub-type is where that data type comes from. The
 * tab lives in ?tab= so a link points at the one being discussed.
 */
export function RuleThresholdsPage() {
  const { t } = useTranslation('app')
  const [searchParams, setSearchParams] = useSearchParams()
  const tab = searchParams.get('tab') === 'sub-types' ? 'sub-types' : 'thresholds'

  const { has } = useCapabilities()
  const canWrite = has('catalog:write')
  const { templates } = useSettingTemplates()
  const qc = useQueryClient()

  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [templateFilter, setTemplateFilter] = useState('')
  const [includeDisabled, setIncludeDisabled] = useState(true)
  const [error, setError] = useState('')

  const [thresholdOpen, setThresholdOpen] = useState(false)
  const [editingThreshold, setEditingThreshold] = useState<RuleThreshold | null>(null)
  const [thresholdForm, setThresholdForm] = useState<ThresholdForm>(emptyThreshold)

  const [subTypeOpen, setSubTypeOpen] = useState(false)
  const [editingSubType, setEditingSubType] = useState<RuleThresholdSubType | null>(null)
  const [subTypeForm, setSubTypeForm] = useState<SubTypeForm>(emptySubType)

  const thresholdParams = {
    search,
    includeDisabled: String(includeDisabled),
    ...(templateFilter ? { templateTypeId: templateFilter } : {}),
  }

  const thresholdQuery = useQuery({
    queryKey: ['threshold-types', thresholdParams],
    queryFn: async (): Promise<RuleThreshold[]> => {
      const res = await api.get('/threshold-types', { params: thresholdParams })
      return (res.data as { thresholdTypes: RuleThreshold[] }).thresholdTypes ?? []
    },
  })

  // Five rows: hiding a retired one saves no space, and it has to be visible to
  // be restored at all.
  const subTypeQuery = useQuery({
    queryKey: ['threshold-sub-types'],
    queryFn: async (): Promise<RuleThresholdSubType[]> => {
      const res = await api.get('/threshold-sub-types', { params: { includeDisabled: 'true' } })
      return (res.data as { thresholdSubTypes: RuleThresholdSubType[] }).thresholdSubTypes ?? []
    },
  })

  const thresholds = thresholdQuery.data ?? []
  const subTypes = subTypeQuery.data ?? []

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['threshold-types'] })
    qc.invalidateQueries({ queryKey: ['threshold-sub-types'] })
    // A setting's thresholds tab names them, so it moves too.
    qc.invalidateQueries({ queryKey: ['setting-thresholds'] })
  }

  const openCreateThreshold = () => {
    setEditingThreshold(null)
    setThresholdForm({ ...emptyThreshold, templateTypeId: templateFilter })
    setError('')
    setThresholdOpen(true)
  }

  const openEditThreshold = (row: RuleThreshold) => {
    setEditingThreshold(row)
    setThresholdForm({
      thresholdType: row.thresholdType,
      templateTypeId: String(row.templateTypeId),
      thresholdSubTypeId: String(row.thresholdSubTypeId),
    })
    setError('')
    setThresholdOpen(true)
  }

  const saveThreshold = useMutation({
    mutationFn: () => {
      const body = {
        thresholdType: thresholdForm.thresholdType.trim(),
        templateTypeId: Number(thresholdForm.templateTypeId),
        thresholdSubTypeId: Number(thresholdForm.thresholdSubTypeId),
        isDisabledByPlatform: editingThreshold?.isDisabledByPlatform ?? false,
      }
      return editingThreshold
        ? api.put(`/threshold-types/${editingThreshold.thresholdTypeId}`, body)
        : api.post('/threshold-types', body)
    },
    onSuccess: () => {
      setThresholdOpen(false)
      setError('')
      invalidate()
    },
    onError: (err) => setError(messageOf(err, t('ruleThresholds.errors.save'))),
  })

  const toggleThreshold = useMutation({
    mutationFn: (row: RuleThreshold) =>
      row.isDisabledByPlatform
        ? api.post(`/threshold-types/${row.thresholdTypeId}/restore`)
        : api.delete(`/threshold-types/${row.thresholdTypeId}`),
    onSuccess: () => {
      setError('')
      invalidate()
    },
    onError: (err) => setError(messageOf(err, t('ruleThresholds.errors.save'))),
  })

  const openCreateSubType = () => {
    setEditingSubType(null)
    setSubTypeForm(emptySubType)
    setError('')
    setSubTypeOpen(true)
  }

  const openEditSubType = (row: RuleThresholdSubType) => {
    setEditingSubType(row)
    setSubTypeForm({ thresholdSubType: row.thresholdSubType, dataType: row.dataType })
    setError('')
    setSubTypeOpen(true)
  }

  const saveSubType = useMutation({
    mutationFn: () => {
      const body = {
        thresholdSubType: subTypeForm.thresholdSubType.trim(),
        dataType: subTypeForm.dataType.trim(),
        isDisabledByPlatform: editingSubType?.isDisabledByPlatform ?? false,
      }
      return editingSubType
        ? api.put(`/threshold-sub-types/${editingSubType.thresholdSubTypeId}`, body)
        : api.post('/threshold-sub-types', body)
    },
    onSuccess: () => {
      setSubTypeOpen(false)
      setError('')
      invalidate()
    },
    onError: (err) => setError(messageOf(err, t('ruleThresholds.errors.save'))),
  })

  const toggleSubType = useMutation({
    mutationFn: (row: RuleThresholdSubType) =>
      row.isDisabledByPlatform
        ? api.post(`/threshold-sub-types/${row.thresholdSubTypeId}/restore`)
        : api.delete(`/threshold-sub-types/${row.thresholdSubTypeId}`),
    onSuccess: () => {
      setError('')
      invalidate()
    },
    onError: (err) => setError(messageOf(err, t('ruleThresholds.errors.save'))),
  })

  const templateItems = templates.map((tpl) => ({ value: String(tpl.id), label: tpl.name }))
  const subTypeItems = subTypes.map((s) => ({
    value: String(s.thresholdSubTypeId),
    label: `${s.thresholdSubType} · ${s.dataType}`,
  }))

  // A threshold a setting already accepts cannot change template: the link
  // would outlive the move, and the setting would hold a limit its own template
  // does not own. The picker says so rather than letting the save fail.
  const templateFrozen = (editingThreshold?.settingCount ?? 0) > 0
  const canSaveThreshold =
    thresholdForm.thresholdType.trim() !== '' &&
    thresholdForm.templateTypeId !== '' &&
    thresholdForm.thresholdSubTypeId !== ''
  const canSaveSubType =
    subTypeForm.thresholdSubType.trim() !== '' && subTypeForm.dataType.trim() !== ''

  return (
    <div>
      <PageHeading
        title={t('ruleThresholds.title')}
        subtitle={t('ruleThresholds.subtitle')}
        action={
          canWrite && (
            <Button onClick={tab === 'thresholds' ? openCreateThreshold : openCreateSubType}>
              <Plus />
              {tab === 'thresholds'
                ? t('ruleThresholds.newThreshold')
                : t('ruleThresholds.newSubType')}
            </Button>
          )
        }
      />

      <HelpBanner storageKey="rule-thresholds">
        <p>{t('ruleThresholds.help.line1')}</p>
        <p>{t('ruleThresholds.help.line2')}</p>
      </HelpBanner>

      <ErrorBanner message={error} />

      <Tabs
        value={tab}
        onValueChange={(value) => {
          searchParams.set('tab', String(value))
          setSearchParams(searchParams, { replace: true })
        }}
      >
        <TabsList>
          <Tab value="thresholds">
            {t('ruleThresholds.tabs.thresholds')}
            <TabCount value={thresholds.length} />
          </Tab>
          <Tab value="sub-types">
            {t('ruleThresholds.tabs.subTypes')}
            <TabCount value={subTypes.length} />
          </Tab>
        </TabsList>

        {/* ---- thresholds ---- */}
        <TabsPanel value="thresholds">
          <CatalogToolbar
            searchInput={searchInput}
            onSearchInput={setSearchInput}
            onSubmitSearch={() => setSearch(searchInput)}
            searchPlaceholder={t('ruleThresholds.searchPlaceholder')}
            includeDisabled={includeDisabled}
            onToggleDisabled={() => setIncludeDisabled(!includeDisabled)}
            filtered={search !== '' || !includeDisabled || templateFilter !== ''}
            onClear={() => {
              setSearch('')
              setSearchInput('')
              setTemplateFilter('')
              setIncludeDisabled(true)
            }}
          >
            <Combobox
              value={templateFilter}
              onValueChange={setTemplateFilter}
              items={templateItems}
              placeholder={t('ruleThresholds.allTemplates')}
              className="w-56"
            />
          </CatalogToolbar>

          {thresholdQuery.isLoading ? (
            <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="w-16">{t('ruleThresholds.columns.id')}</TableHead>
                    <TableHead>{t('ruleThresholds.columns.name')}</TableHead>
                    <TableHead className="w-44">{t('ruleThresholds.columns.template')}</TableHead>
                    <TableHead className="w-40">{t('ruleThresholds.columns.subType')}</TableHead>
                    <TableHead className="w-32">{t('ruleThresholds.columns.settings')}</TableHead>
                    <TableHead className="w-28">{t('common.columns.status')}</TableHead>
                    {canWrite && (
                      <TableHead className="w-28 text-right">
                        {t('common.columns.actions')}
                      </TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {thresholds.map((row) => (
                    <TableRow key={row.thresholdTypeId}>
                      <TableCell className="font-mono text-xs text-muted-foreground">
                        {row.thresholdTypeId}
                      </TableCell>
                      <TableCell className="font-medium">{row.thresholdType}</TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground">
                        {row.templateType || `#${row.templateTypeId}`}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{row.dataType || `#${row.thresholdSubTypeId}`}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {t('ruleThresholds.settingCount', { count: row.settingCount })}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <StatusBadge disabled={row.isDisabledByPlatform} />
                      </TableCell>
                      {canWrite && (
                        <TableCell>
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              title={t('common.edit')}
                              onClick={() => openEditThreshold(row)}
                            >
                              <Pencil />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              title={
                                row.isDisabledByPlatform ? t('common.restore') : t('common.retire')
                              }
                              disabled={toggleThreshold.isPending}
                              onClick={() => toggleThreshold.mutate(row)}
                            >
                              {row.isDisabledByPlatform ? <RotateCcw /> : <Ban />}
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                  {thresholds.length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={canWrite ? 7 : 6}
                        className="py-8 text-center text-muted-foreground"
                      >
                        {t('ruleThresholds.empty')}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsPanel>

        {/* ---- sub-types ---- */}
        <TabsPanel value="sub-types">
          <p className="mb-4 max-w-3xl text-sm text-muted-foreground">
            {t('ruleThresholds.subTypeHint')}
          </p>

          {subTypeQuery.isLoading ? (
            <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="w-16">{t('ruleThresholds.columns.id')}</TableHead>
                    <TableHead>{t('ruleThresholds.columns.subTypeName')}</TableHead>
                    <TableHead className="w-40">{t('ruleThresholds.columns.dataType')}</TableHead>
                    <TableHead className="w-40">{t('ruleThresholds.columns.thresholds')}</TableHead>
                    <TableHead className="w-28">{t('common.columns.status')}</TableHead>
                    {canWrite && (
                      <TableHead className="w-28 text-right">
                        {t('common.columns.actions')}
                      </TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {subTypes.map((row) => (
                    <TableRow key={row.thresholdSubTypeId}>
                      <TableCell className="font-mono text-xs text-muted-foreground">
                        {row.thresholdSubTypeId}
                      </TableCell>
                      <TableCell className="font-medium">{row.thresholdSubType}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{row.dataType}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {t('ruleThresholds.thresholdCount', { count: row.thresholdCount })}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <StatusBadge disabled={row.isDisabledByPlatform} />
                      </TableCell>
                      {canWrite && (
                        <TableCell>
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              title={t('common.edit')}
                              onClick={() => openEditSubType(row)}
                            >
                              <Pencil />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              title={
                                row.isDisabledByPlatform ? t('common.restore') : t('common.retire')
                              }
                              disabled={toggleSubType.isPending}
                              onClick={() => toggleSubType.mutate(row)}
                            >
                              {row.isDisabledByPlatform ? <RotateCcw /> : <Ban />}
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsPanel>
      </Tabs>

      {/* ---- threshold dialog ---- */}
      <Dialog open={thresholdOpen} onOpenChange={setThresholdOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingThreshold
                ? t('ruleThresholds.editThreshold')
                : t('ruleThresholds.newThreshold')}
            </DialogTitle>
          </DialogHeader>
          <form
            className="flex flex-col gap-4"
            onSubmit={(e) => {
              e.preventDefault()
              if (canSaveThreshold) saveThreshold.mutate()
            }}
          >
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="threshold-name">{t('ruleThresholds.columns.name')}</Label>
              <Input
                id="threshold-name"
                value={thresholdForm.thresholdType}
                onChange={(e) =>
                  setThresholdForm((f) => ({ ...f, thresholdType: e.target.value }))
                }
                maxLength={50}
                placeholder="Max value daily"
                autoFocus
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label>{t('ruleThresholds.columns.template')}</Label>
              <Combobox
                value={thresholdForm.templateTypeId}
                onValueChange={(v) => setThresholdForm((f) => ({ ...f, templateTypeId: v }))}
                items={templateItems}
                placeholder={t('ruleThresholds.pickTemplate')}
                className="w-full"
                disabled={templateFrozen}
              />
              {templateFrozen && (
                <p className="text-xs text-muted-foreground">
                  {t('ruleThresholds.templateFrozen', {
                    count: editingThreshold?.settingCount ?? 0,
                  })}
                </p>
              )}
            </div>

            <div className="flex flex-col gap-1.5">
              <Label>{t('ruleThresholds.columns.subType')}</Label>
              <Combobox
                value={thresholdForm.thresholdSubTypeId}
                onValueChange={(v) => setThresholdForm((f) => ({ ...f, thresholdSubTypeId: v }))}
                items={subTypeItems}
                placeholder={t('ruleThresholds.pickSubType')}
                className="w-full"
              />
              <p className="text-xs text-muted-foreground">{t('ruleThresholds.subTypeFieldHint')}</p>
            </div>

            <ErrorBanner message={error} />

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setThresholdOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={saveThreshold.isPending || !canSaveThreshold}>
                {editingThreshold ? t('common.save') : t('common.create')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* ---- sub-type dialog ---- */}
      <Dialog open={subTypeOpen} onOpenChange={setSubTypeOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingSubType ? t('ruleThresholds.editSubType') : t('ruleThresholds.newSubType')}
            </DialogTitle>
          </DialogHeader>
          <form
            className="flex flex-col gap-4"
            onSubmit={(e) => {
              e.preventDefault()
              if (canSaveSubType) saveSubType.mutate()
            }}
          >
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="sub-type-name">{t('ruleThresholds.columns.subTypeName')}</Label>
              <Input
                id="sub-type-name"
                value={subTypeForm.thresholdSubType}
                onChange={(e) =>
                  setSubTypeForm((f) => ({ ...f, thresholdSubType: e.target.value }))
                }
                maxLength={50}
                placeholder="Currency"
                autoFocus
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="sub-type-data">{t('ruleThresholds.columns.dataType')}</Label>
              <Input
                id="sub-type-data"
                value={subTypeForm.dataType}
                onChange={(e) => setSubTypeForm((f) => ({ ...f, dataType: e.target.value }))}
                maxLength={10}
                placeholder="currency"
                className="font-mono"
              />
              <p className="text-xs text-muted-foreground">{t('ruleThresholds.dataTypeHint')}</p>
            </div>

            <ErrorBanner message={error} />

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setSubTypeOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={saveSubType.isPending || !canSaveSubType}>
                {editingSubType ? t('common.save') : t('common.create')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}
